import { DataLoaderParams } from '@/types/DataLoaderParams';
import { fetchBidIncrementsIfNeeded } from '@/redux/modules/bidIncrements';
import { fetchBidLimitBalanceIfNeeded } from '@/redux/modules/bidLimit';
import { fetchCatalogDetailsIfNeeded } from '@/redux/modules/catalogDetail';
import { fetchCatalogItemModels } from '@/redux/modules/catalogItems';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalog.actions';
import { fetchConsoleBrandingIfNeeded } from '@/redux/modules/consoleBranding';
import { fetchItemDetailsIfNeeded, getItemDetail } from '@/redux/modules/itemDetail';
import { fetchItemShippingsIfNeeded } from '@/redux/modules/itemShipping';
import { fetchListingAgentsIfNeeded } from '@/redux/modules/listingAgent';
import { fetchLiveCatalogRegistrationIfNeeded } from '@/redux/modules/catalogRegistration';
import { fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { fetchSavedItemCountsIfNeeded } from '@/redux/modules/savedItemCount';
import { fetchSellersDetailsIfNeeded } from '@/redux/modules/sellerDetail';
import { fetchStreamIdIfNeeded } from '@/redux/modules/video';
import { fetchTopSellersIfNeeded } from '@/redux/modules/topRatedSellers';
import { getCatalog } from '@/redux/modules/catalog/catalog.selectors';
import { getIdFromPath, getNotFoundUrl } from '@/utils/urls';
import { getLiveCatalogData } from '@/redux/modules/console';
import { push } from 'connected-react-router';
import TEST_HOUSES from '@/utils/testHouses';
import type { FetchItemsCardModelsByCatalogIdQueryType } from '@/redux/api/itemCardModel';

const BiddingConsolePageData = async ({ location, store }: DataLoaderParams) => {
    const { pathname } = location;
    const { dispatch, getState } = store;

    // If we dont have the catalogId here then we're going to abort
    const catalogId = getIdFromPath(pathname);
    if (!catalogId) {
        return dispatch(push(getNotFoundUrl()));
    }

    // fetch catalog to get sellerId
    await dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));

    // fetch live catalog status
    const { sellerId } = getCatalog(getState(), catalogId);
    await dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId, true));

    // If catalog is live, grab data for console
    const { catalogStatus } = getCatalog(getState(), catalogId);
    if (catalogStatus !== 'done') {
        const query: FetchItemsCardModelsByCatalogIdQueryType = {
            pageSize: 'all',
            preview: TEST_HOUSES.includes(sellerId),
            sort: 'lotNumber',
        };

        // catalog/console meta/seller data dependencies
        const catalogData = [
            dispatch(fetchBidIncrementsIfNeeded(catalogId)),
            dispatch(fetchCatalogDetailsIfNeeded([catalogId])),
            dispatch(fetchCatalogItemModels({ catalogId, query })),
            dispatch(fetchConsoleBrandingIfNeeded(catalogId, sellerId)),
            dispatch(fetchLiveCatalogRegistrationIfNeeded(catalogId, sellerId)),
            dispatch(fetchSellersDetailsIfNeeded([sellerId])),
            dispatch(fetchStreamIdIfNeeded(catalogId)),
            dispatch(fetchTopSellersIfNeeded()),
        ];

        // current item/user data dependencies
        const { currentItem: itemId } = getLiveCatalogData(getState(), catalogId);
        const itemData = [
            dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, force: true, itemId })),
            dispatch(fetchItemDetailsIfNeeded([itemId])),
            dispatch(fetchItemShippingsIfNeeded([itemId])),
            dispatch(fetchSavedItemCountsIfNeeded([itemId])),
        ];

        // fetch the catalog + current item data
        await Promise.all([...catalogData, ...itemData]);

        // if there is a listing agent, fetch it's details
        const { agentId } = getItemDetail(getState(), catalogId);
        if (agentId) {
            await dispatch(fetchListingAgentsIfNeeded([agentId]));
        }
    }
};

export default BiddingConsolePageData;
